<script>
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';

export default {
  name: 'AddTranslation',
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
  },
  props: {
    cannedResponseId: { type: Number, required: true },
    locale: { type: String, required: true },
    localeName: { type: String, required: true },
    existingContent: { type: String, default: '' },
    translationId: { type: Number, default: null },
    onClose: { type: Function, default: () => {} },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      content: this.existingContent || '',
      isLoading: false,
      show: true,
    };
  },
  computed: {
    isEditMode() {
      return !!this.translationId;
    },
    modalTitle() {
      return this.isEditMode 
        ? `Edit ${this.localeName} Translation` 
        : `Add ${this.localeName} Translation`;
    },
  },
  validations: {
    content: {
      required,
    },
  },
  methods: {
    async saveTranslation() {
      this.isLoading = true;
      
      try {
        if (this.isEditMode) {
          // Update existing translation
          await this.$store.dispatch('updateCannedResponseTranslation', {
            cannedResponseId: this.cannedResponseId,
            translationId: this.translationId,
            locale: this.locale,
            content: this.content,
          });
          useAlert(`Translation for ${this.localeName} updated successfully!`);
        } else {
          // Create new translation
          await this.$store.dispatch('createCannedResponseTranslation', {
            cannedResponseId: this.cannedResponseId,
            locale: this.locale,
            content: this.content,
          });
          useAlert(`Translation for ${this.localeName} added successfully!`);
        }

        this.onClose();
      } catch (error) {
        useAlert(error?.message || 'Failed to save translation');
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="modalTitle"
        :header-content="`${isEditMode ? 'Edit' : 'Add'} the ${localeName} version of this template`"
      />
      <form class="flex flex-col w-full p-6" @submit.prevent="saveTranslation()">
        <div class="w-full">
          <label :class="{ error: v$.content.$error }">
            Message ({{ localeName }})
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              :class="{ editor_warning: v$.content.$error }"
              enable-variables
              :enable-canned-responses="false"
              placeholder="Enter the translated message..."
              @blur="v$.content.$touch"
            />
          </div>
          <p class="text-xs text-slate-600 mt-1">
            Use {variable_name} for variables (e.g., {customer_name}, {order_number})<br>
            For links: Plain URLs auto-link, or use {link:https://example.com|Click here}
          </p>
        </div>
        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2 mt-4">
          <NextButton
            faded
            slate
            type="reset"
            label="Cancel"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            label="Save Translation"
            :disabled="v$.content.$invalid || isLoading"
            :is-loading="isLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
::v-deep {
  .ProseMirror-menubar {
    @apply hidden;
  }

  .ProseMirror-woot-style {
    @apply min-h-[12.5rem];

    p {
      @apply text-base;
    }
  }
}
</style>
